import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, TextInput } from 'react-native';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AsyncStorage from '../AsyncStorageShim';
import { useTranslation } from 'react-i18next';
import ListsManager from '../services/ListsManager';
import { FaArrowLeft, FaSearch, FaPlus, FaTrash, FaUserEdit, FaTimes, FaGlobe } from 'react-icons/fa';
import Modal from '../components/Modal';
import FollowNewUserScreen from './FollowNewUserScreen';

// User Cell Component
const UserCell = ({ user, isAddCell, onDelete, onPrompt, onPress }) => {
  const { t } = useTranslation();
  return (
    <TouchableOpacity
      style={[styles.userCell, isAddCell && styles.addUserCell]}
      onPress={isAddCell ? onPress : null}
      disabled={!isAddCell}
    >
      <View style={styles.userInfoContainer}>
        {isAddCell ? (
          <View style={[styles.avatar, styles.addAvatar]}>
            <FaPlus style={styles.addAvatarIcon} />
          </View>
        ) : (
          <View style={styles.avatar}>
            {user.profile_image_url ? (
              <img
                src={user.profile_image_url}
                alt={user.username}
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <Text style={styles.avatarText}>
                {user.username ? user.username.charAt(0).toUpperCase() : '?'}
              </Text>
            )}
          </View>
        )}
        <Text style={[styles.userCellText, isAddCell && styles.addUserText]}>
          {isAddCell ? t('app.lists.addNewUser') : user.username}
        </Text>
      </View>

      {!isAddCell && user.is_removable && (
        <View style={styles.actionsContainer}>
          <TouchableOpacity onPress={() => onPrompt(user)} style={styles.actionButton}>
            <FaUserEdit style={styles.actionButtonIcon} />
          </TouchableOpacity>
          <TouchableOpacity onPress={() => onDelete(user)} style={styles.actionButton}>
            <FaTrash style={styles.actionButtonIcon} />
          </TouchableOpacity>
        </View>
      )}
    </TouchableOpacity>
  );
};

// Modal Component for Special Requests
const SpecialRequestModal = ({ visible, username, specialRequest, onClose, onSave }) => {
  const { t } = useTranslation();
  const [editedRequest, setEditedRequest] = useState(specialRequest);

  useEffect(() => {
    setEditedRequest(specialRequest);
  }, [specialRequest]);

  if (!visible) return null;

  return (
    <View style={styles.modalOverlay}>
      <View style={styles.modalContainer}>
        <View style={styles.modalContent}>
          <Text style={styles.modalTitle}>{t('app.lists.specialRequestFor', { username })}</Text>
          <TextInput
            style={styles.modalInput}
            value={editedRequest}
            onChangeText={setEditedRequest}
            multiline
            placeholder={t('app.lists.enterSpecialRequest')}
            placeholderTextColor="#888"
          />
          <View style={styles.modalButtonsContainer}>
            <TouchableOpacity style={styles.modalButton} onPress={onClose}>
              <FaTimes style={styles.modalButtonIcon} />
              <Text style={styles.modalButtonText}>{t('app.lists.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.modalButton, styles.saveButton]}
              onPress={() => onSave(editedRequest)}
            >
              <FaUserEdit style={styles.modalButtonIcon} />
              <Text style={styles.modalButtonText}>{t('app.lists.save')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};

const ListUsersScreen = ({ listId, initialListName, onClose, isModal }) => {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const id = listId || paramId;
  const listName = initialListName || location.state?.listName;

  // State for Twitter, Telegram, TikTok, Instagram, and RSS users
  const [twitterUsers, setTwitterUsers] = useState([]);
  const [telegramUsers, setTelegramUsers] = useState([]);
  const [tiktokUsers, setTiktokUsers] = useState([]);
  const [instagramUsers, setInstagramUsers] = useState([]);
  const [facebookPagesUsers, setFacebookPagesUsers] = useState([]);
  const [rssFeeds, setRssFeeds] = useState([]);
  const [filteredTwitterUsers, setFilteredTwitterUsers] = useState([]);
  const [filteredTelegramUsers, setFilteredTelegramUsers] = useState([]);
  const [filteredTiktokUsers, setFilteredTiktokUsers] = useState([]);
  const [filteredInstagramUsers, setFilteredInstagramUsers] = useState([]);
  const [filteredFacebookPagesUsers, setFilteredFacebookPagesUsers] = useState([]);
  const [filteredRssFeeds, setFilteredRssFeeds] = useState([]);

  // Loading, error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');

  // Special request modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [specialRequest, setSpecialRequest] = useState('');

  useEffect(() => {
    loadUsers();
  }, [id]);

  useEffect(() => {
    filterUsers();
  }, [searchText, twitterUsers, telegramUsers, tiktokUsers, instagramUsers, facebookPagesUsers, rssFeeds]);

  // Add a new useEffect to check if we're returning from FollowNewUserScreen
  useEffect(() => {
    // Check if we're returning from FollowNewUserScreen with a new user
    if (location.state?.refreshUsers) {
      loadUsers();
      // Clear the navigation state to prevent multiple refreshes
      navigate(location.pathname, { replace: true });
    }
  }, [location.state]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);


      // Fetch sources
      const [twUsers, tgUsers, ttUsers, igUsers, fbPages, rssFollows] = await Promise.all([
        ListsManager.fetchTwitterUsernamesFromList(id),
        ListsManager.getTelegramChannels(id),
        ListsManager.getTikTokChannels(id),
        ListsManager.getInstagramProfiles(id),
        ListsManager.getFacebookPages(id),
        ListsManager.getRssFollows(id)
      ]);

      const normalizedTW = (twUsers || []).map(u => ({ ...u, source: 'twitter' }));
      const normalizedTG = (tgUsers || []).map(u => ({ ...u, source: 'telegram' }));
      const normalizedTT = (ttUsers || []).map(u => ({ ...u, source: 'tiktok' }));
      const normalizedIG = (igUsers || []).map(u => ({ ...u, source: 'instagram' }));
      const normalizedFP = (fbPages || []).map(u => ({ ...u, source: 'facebook_page' }));
      const normalizedRSS = (rssFollows || []).map(u => ({ ...u, source: 'rss' }));

      setTwitterUsers(normalizedTW);
      setTelegramUsers(normalizedTG);
      setTiktokUsers(normalizedTT);
      setInstagramUsers(normalizedIG);
      setFacebookPagesUsers(normalizedFP);
      setRssFeeds(normalizedRSS);
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading list users:', error);
      setError(t('app.lists.failedToLoadUsers'));
      setLoading(false);
    }
  };

  const filterUsers = () => {
    const query = searchText.toLowerCase().trim();
    if (query === '') {
      setFilteredTwitterUsers(twitterUsers);
      setFilteredTelegramUsers(telegramUsers);
      setFilteredTiktokUsers(tiktokUsers);
      setFilteredInstagramUsers(instagramUsers);
      setFilteredFacebookPagesUsers(facebookPagesUsers);
      setFilteredRssFeeds(rssFeeds);
    } else {
      setFilteredTwitterUsers(twitterUsers.filter(u => u.username?.toLowerCase().includes(query)));
      setFilteredTelegramUsers(telegramUsers.filter(u => u.username?.toLowerCase().includes(query)));
      setFilteredTiktokUsers(tiktokUsers.filter(u => u.username?.toLowerCase().includes(query)));
      setFilteredInstagramUsers(instagramUsers.filter(u => u.username?.toLowerCase().includes(query)));
      setFilteredFacebookPagesUsers(facebookPagesUsers.filter(u => u.username?.toLowerCase().includes(query)));
      setFilteredRssFeeds(rssFeeds.filter(u => u.username?.toLowerCase().includes(query)));
    }
  };

  const handleSearch = (text) => {
    setSearchText(text);
  };

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const handleDeleteUser = (user) => {
    if (window.confirm(t('app.lists.confirmRemoveUser', { username: user.username }))) {
      deleteUser(user);
    }
  };

  const deleteUser = async (user) => {
    try {
      let success = false;
      if (user.source === 'twitter') {
        success = await ListsManager.deleteUserFromList(id, user.username);
      } else if (user.source === 'telegram') {
        success = await ListsManager.deleteTelegramChannel(id, user.username);
      } else if (user.source === 'tiktok') {
        success = await ListsManager.deleteTikTokChannel(id, user.username);
      } else if (user.source === 'instagram') {
        success = await ListsManager.deleteInstagramProfile(id, user.username);
      } else if (user.source === 'facebook_page') {
        success = await ListsManager.deleteFacebookPage(id, user.username);
      } else if (user.source === 'rss') {
        success = await ListsManager.deleteRssFeed(id, user.username);
      }

      if (success) {
        loadUsers();
      } else {
        alert(t('app.lists.failedToDelete', { username: user.username }));
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(t('app.lists.errorDeleting', { username: user.username, error: error.message }));
    }
  };

  const handleFetchSpecialRequest = async (user) => {
    try {
      const fetchedRequest = await ListsManager.fetchSpecialRequest(id, user.username);
      setSelectedUser(user);
      setSpecialRequest(fetchedRequest || '');
      setModalVisible(true);
    } catch (error) {
      console.error("Error fetching special request:", error);
      alert(t('app.lists.failedToFetchSpecialRequest'));
    }
  };

  const handleSaveSpecialRequest = async (newRequest) => {
    if (!selectedUser) return;

    try {
      const success = await ListsManager.updateSpecialRequest(
        id,
        selectedUser.username,
        newRequest
      );

      if (success) {
        alert(t('app.lists.specialRequestUpdated'));
      } else {
        alert(t('app.lists.failedToUpdateSpecialRequest'));
      }

      setModalVisible(false);
    } catch (error) {
      console.error("Error updating special request:", error);
      alert(t('app.lists.failedToUpdateSpecialRequest'));
    }
  };

  const handleUserAdded = (newUser, fromTwitter) => {
    loadUsers();
  };

  const handleAddUser = () => {
    setShowAddUserModal(true);
  };

  const renderHeader = (title) => (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionHeaderText}>{title}</Text>
    </View>
  );

  if (showAddUserModal) {
    return (
      <View style={{ flex: 1, backgroundColor: 'var(--bg-deep-void)' }}>
        <FollowNewUserScreen
          listID={id}
          listName={listName}
          onUserAdded={(newUser, fromTwitter) => {
            handleUserAdded(newUser, fromTwitter);
          }}
          onClose={() => {
            setShowAddUserModal(false);
          }}
          isModal={false}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {!isModal && (
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <FaArrowLeft style={styles.backButtonIcon} />
            <Text style={styles.backButtonText}>{t('app.lists.back')}</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{listName || t('app.lists.list')} {t('app.lists.users')}</Text>
          <View style={{ width: 50 }} />
        </View>
      )}

      <View style={styles.searchContainer}>
        <View style={styles.searchInputWrapper}>
          <FaSearch style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder={t('app.lists.searchUsers')}
            placeholderTextColor="#888"
            value={searchText}
            onChangeText={handleSearch}
          />
          {searchText ? (
            <TouchableOpacity onPress={() => handleSearch('')}>
              <FaTimes style={styles.clearIcon} />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>

      {error ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : (
        <ScrollView style={styles.content}>
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#4DA6FF" />
              <Text style={styles.loadingText}>{t('app.lists.loadingUsers')}</Text>
            </View>
          ) : (
            <>
              {/* Add User Button */}
              <UserCell
                user={{ username: "Add a new user", is_removable: false }}
                isAddCell={true}
                onPress={handleAddUser}
              />

              {/* Twitter Users Section */}
              {filteredTwitterUsers.length > 0 && (
                <>
                  {renderHeader(t('app.lists.twitterUsers'))}
                  {filteredTwitterUsers.map((user, index) => (
                    <UserCell
                      key={`twitter-${index}`}
                      user={user}
                      isAddCell={false}
                      onDelete={handleDeleteUser}
                      onPrompt={handleFetchSpecialRequest}
                    />
                  ))}
                </>
              )}

              {/* Telegram Users Section */}
              {filteredTelegramUsers.length > 0 && (
                <>
                  {renderHeader(t('app.lists.telegramChannels'))}
                  {filteredTelegramUsers.map((user, index) => (
                    <UserCell
                      key={`telegram-${index}`}
                      user={user}
                      isAddCell={false}
                      onDelete={handleDeleteUser}
                      onPrompt={handleFetchSpecialRequest}
                    />
                  ))}
                </>
              )}

              {/* TikTok Users Section */}
              {filteredTiktokUsers.length > 0 && (
                <>
                  {renderHeader(t('app.lists.tiktokChannels'))}
                  {filteredTiktokUsers.map((user, index) => (
                    <UserCell
                      key={`tiktok-${index}`}
                      user={user}
                      isAddCell={false}
                      onDelete={handleDeleteUser}
                      onPrompt={handleFetchSpecialRequest}
                    />
                  ))}
                </>
              )}

              {/* Instagram Users Section */}
              {filteredInstagramUsers.length > 0 && (
                <>
                  {renderHeader(t('app.lists.instagramProfiles'))}
                  {filteredInstagramUsers.map((user, index) => (
                    <UserCell
                      key={`instagram-${index}`}
                      user={user}
                      isAddCell={false}
                      onDelete={handleDeleteUser}
                      onPrompt={handleFetchSpecialRequest}
                    />
                  ))}
                </>
              )}

              {/* Facebook Pages Section */}
              {filteredFacebookPagesUsers.length > 0 && (
                <>
                  {renderHeader(t('app.lists.facebookPages', 'Facebook Pages'))}
                  {filteredFacebookPagesUsers.map((user, index) => (
                    <UserCell
                      key={`facebook_page-${index}`}
                      user={user}
                      isAddCell={false}
                      onDelete={handleDeleteUser}
                      onPrompt={handleFetchSpecialRequest}
                    />
                  ))}
                </>
              )}

              {/* Web Sources Section */}
              {filteredRssFeeds.length > 0 && (
                <>
                  {renderHeader(t('app.lists.webSources'))}
                  {filteredRssFeeds.map((user, index) => (
                    <View key={`rss-${index}`} style={styles.userCell}>
                      <View style={styles.userInfoContainer}>
                        <View style={[styles.avatar, styles.rssAvatar]}>
                          <FaGlobe style={{ color: '#22c55e', fontSize: 16 }} />
                        </View>
                        <Text style={styles.userCellText}>{user.username}</Text>
                      </View>
                      {user.is_removable && (
                        <View style={styles.actionsContainer}>
                          <TouchableOpacity onPress={() => handleDeleteUser(user)} style={styles.actionButton}>
                            <FaTrash style={styles.actionButtonIcon} />
                          </TouchableOpacity>
                        </View>
                      )}
                    </View>
                  ))}
                </>
              )}

              {filteredTwitterUsers.length === 0 && filteredTelegramUsers.length === 0 && filteredTiktokUsers.length === 0 && filteredInstagramUsers.length === 0 && filteredFacebookPagesUsers.length === 0 && filteredRssFeeds.length === 0 && (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyText}>
                    {searchText ? t('app.lists.noUsersFound') : t('app.lists.noUsersInList')}
                  </Text>
                  <TouchableOpacity style={styles.addEmptyButton} onPress={handleAddUser}>
                    <FaPlus style={styles.addEmptyButtonIcon} />
                    <Text style={styles.addEmptyButtonText}>{t('app.lists.addUser')}</Text>
                  </TouchableOpacity>
                </View>
              )}
            </>
          )}
        </ScrollView>
      )}

      {/* Special Request Modal */}
      <SpecialRequestModal
        visible={modalVisible}
        username={selectedUser?.username || ''}
        specialRequest={specialRequest}
        onClose={() => setModalVisible(false)}
        onSave={handleSaveSpecialRequest}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'var(--bg-deep-void)',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.08)',
    backgroundColor: 'var(--bg-card)',
    zIndex: 10,
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(12px)',
  },
  headerTitle: {
    color: 'var(--text-primary)',
    fontSize: 20,
    fontWeight: '700',
    letterSpacing: '-0.01em',
    fontFamily: 'Outfit, sans-serif',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s',
  },
  backButtonIcon: {
    color: 'var(--text-primary)',
    fontSize: 14,
    marginEnd: 8,
  },
  backButtonText: {
    color: 'var(--text-primary)',
    fontSize: 14,
    fontWeight: '600',
  },
  searchContainer: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
  },
  searchInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
  },
  searchIcon: {
    color: 'var(--text-secondary)',
    fontSize: 14,
    marginEnd: 12,
  },
  searchInput: {
    flex: 1,
    color: 'var(--text-primary)',
    fontSize: 16,
    outlineStyle: 'none',
  },
  clearIcon: {
    color: 'var(--text-muted)',
    fontSize: 14,
    padding: 4,
  },
  content: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  loadingContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
  },
  loadingText: {
    color: 'var(--text-secondary)',
    marginTop: 16,
    fontSize: 16,
  },
  errorContainer: {
    padding: 16,
    margin: 16,
    borderWidth: 1,
    borderColor: 'rgba(244, 63, 94, 0.3)',
    borderRadius: 16,
    backgroundColor: 'rgba(244, 63, 94, 0.1)',
  },
  errorText: {
    color: 'var(--accent-warning)',
    fontSize: 16,
    textAlign: 'center',
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 300,
  },
  emptyText: {
    color: 'var(--text-muted)',
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
  },
  addEmptyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'var(--accent-primary)',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 20,
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
    transition: 'all 0.2s',
  },
  addEmptyButtonIcon: {
    color: 'white',
    fontSize: 14,
    marginEnd: 8,
  },
  addEmptyButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  sectionHeader: {
    padding: 12,
    paddingHorizontal: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
  },
  sectionHeaderText: {
    color: 'var(--text-secondary)',
    fontSize: 13,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  userInfoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 16,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.3)',
    overflow: 'hidden',
  },
  avatarText: {
    color: 'var(--accent-primary)',
    fontSize: 18,
    fontWeight: '600',
  },
  addAvatar: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderStyle: 'dashed',
  },
  rssAvatar: {
    backgroundColor: 'rgba(34, 197, 94, 0.15)',
    borderColor: 'rgba(34, 197, 94, 0.3)',
  },
  addAvatarIcon: {
    color: 'var(--accent-primary)',
    fontSize: 14,
  },
  addUserText: {
    color: 'var(--accent-primary)',
    fontWeight: '600',
  },
  userCell: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12, // Reduced padding to match compact style
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
    backgroundColor: 'var(--bg-card)',
    transition: 'background-color 0.2s',
  },
  addUserCell: {
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    borderRadius: 16,
    marginHorizontal: 24,
    marginVertical: 16,
    borderWidth: 1,
    borderColor: 'rgba(59, 130, 246, 0.3)',
    borderBottomWidth: 1,
  },
  userCellText: {
    color: 'var(--text-primary)',
    fontSize: 16,
    fontWeight: '500',
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    marginStart: 12,
    padding: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 12,
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  actionButtonIcon: {
    fontSize: 14,
    color: 'var(--text-secondary)',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--overlay)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContainer: {
    width: '90%',
    maxWidth: 500,
  },
  modalContent: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: 24,
    padding: 32,
    boxShadow: 'var(--shadow-glass)',
    backdropFilter: 'blur(20px)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
  },
  modalTitle: {
    color: 'var(--text-primary)',
    fontSize: 22,
    fontWeight: '700',
    marginBottom: 24,
    textAlign: 'center',
    fontFamily: 'Outfit, sans-serif',
  },
  modalInput: {
    backgroundColor: 'var(--bg-card)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 16,
    padding: 16,
    color: 'var(--text-primary)',
    height: 120,
    textAlignVertical: 'top',
    marginBottom: 24,
    fontSize: 16,
    outlineStyle: 'none',
  },
  modalButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 16,
  },
  modalButton: {
    flex: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    padding: 14,
    borderRadius: 16,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  modalButtonIcon: {
    color: 'var(--text-primary)',
    fontSize: 14,
    marginEnd: 8,
  },
  saveButton: {
    backgroundColor: 'var(--accent-primary)',
    borderColor: 'transparent',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  },
  modalButtonText: {
    color: 'var(--text-primary)',
    fontWeight: '600',
    fontSize: 16,
  },
});

export default ListUsersScreen; 